import useFocusTrap from '@charlietango/use-focus-trap';
import React, { FC, ReactNode, useCallback } from 'react';
import styled from 'styled-components';

import { cornerRadiusPx } from '#/service/constants';

import { ReactPortal } from '../Portal';
import { CloseButton } from './CloseButton';
import { FullScreenBackground } from './styled';

export interface ModalProps {
  /** Determines if modal should be visible. Please note that you should not be using `isOpen` for modals with state. In such cases you'd rather use @code `{shouldShowModal && <YourModal />}` instead. */
  isOpen: boolean;

  onClose?: () => void;

  /**
   * If `true`, the modal will close when user clicks overlay
   * @default false
   */
  closeOnOverlayClick?: boolean;

  /**
   * If `true`, the modal will close when the `Esc` key is pressed
   * @default true
   */
  closeOnEsc?: boolean;

  /**
   * If `true`, there will be a close button in the top right part of the modal
   * @default true
   */
  showCloseButton?: boolean;

  /** Children will be included between the message and the close button */
  children?: ReactNode;

  /** Custom aria role used by the modal. @default dialog */
  role?: 'dialog' | 'alert' | 'alertdialog';

  /** @default 'theme' Set to 'static' to use a static shadow background color that is theme-independent */
  overlayBackgroundType?: 'theme' | 'static';

  /** @default 'solid' Set to 'transparent` to remove the background of the modal */
  modalBackgroundType?: 'solid' | 'transparent';
}

export const Modal: FC<ModalProps> = ({
  isOpen,
  children,
  showCloseButton = true,
  onClose,
  closeOnOverlayClick = false,
  closeOnEsc = true,
  role = 'dialog',
  overlayBackgroundType = 'theme',
  modalBackgroundType = 'solid',
  ...rest
}) => {
  const ref = useFocusTrap();

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Escape' && closeOnEsc && onClose) {
        ev.stopPropagation();
        onClose();
      }
    },
    [closeOnEsc, onClose]
  );

  const onOverlayClick = useCallback(
    (ev: React.MouseEvent<HTMLDivElement>) => {
      const target = ev.target as HTMLDivElement;

      if (
        closeOnOverlayClick &&
        target instanceof HTMLDivElement &&
        target.dataset['backdrop'] &&
        onClose
      ) {
        ev.stopPropagation();
        onClose();
      }
    },
    [onClose, closeOnOverlayClick]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <ReactPortal>
      <StyledOverlay
        $modalBackgroundType={modalBackgroundType}
        $overlayType={overlayBackgroundType}
        data-backdrop
        data-nosnippet
        data-testid="modal-backdrop"
        onClick={onOverlayClick}
        onKeyDown={onKeyDown}
        ref={ref}
      >
        <ModalBody
          $modalBackgroundType={modalBackgroundType}
          role={role}
          {...rest}
        >
          {showCloseButton && <CloseButton onClick={onClose} />}
          <div data-testid="modal-body">{children}</div>
        </ModalBody>
      </StyledOverlay>
    </ReactPortal>
  );
};

interface OverlayProps {
  $overlayType: ModalProps['overlayBackgroundType'];
  $modalBackgroundType: ModalProps['modalBackgroundType'];
}

// TODO: Revisit modal backdrop colors and sync with design team if it
// should be a theme driven or fixed color.
// The current implementation is temporary and was made by assumption
const StyledOverlay = styled(FullScreenBackground)<OverlayProps>`
  ${({ $overlayType, $modalBackgroundType }) =>
    $overlayType === 'static' && $modalBackgroundType === 'solid'
      ? 'background-color: var(--overlayBackdrop)'
      : 'background-color: var(--overlayBackdropDarker)'};
`;

interface ModalBodyProps {
  $modalBackgroundType: ModalProps['modalBackgroundType'];
}
const ModalBody = styled.div<ModalBodyProps>`
  height: auto;
  background-color: ${({ $modalBackgroundType }) =>
    $modalBackgroundType === 'solid'
      ? 'var(--pageSecondaryBackground)'
      : 'none'};
  color: var(--pageParagraphForeground);
  text-align: center;
  border-radius: ${({ theme }) => cornerRadiusPx[theme.cornerRadius]}px;
  position: relative;
  z-index: 100;
  padding: 1.5rem;
`;
